'use client';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import type { Action, FighterInit, RoundEvent, Team } from '@cs-shot/battle-sim';
import { fighterLayout } from '@cs-shot/battle-sim';
import { cn } from '../../../utils/cn';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useHaptic } from '../../../hooks/useHaptic';
import { FaBolt } from '../../icons';
import { ActionMenu, type Stance } from './ActionMenu';
import { loadBattleAssets, type BattleAssets } from './battleAssets';
import { RoundLogSheet } from './RoundLogSheet';
import translations from './RushMid.translations';
import type { RoundUpdate } from './useRushMidMatch';
import { useBattleCanvas, WORLD_W, WORLD_H, ACTION_BAR_RESERVE } from './useBattleCanvas';
import { useRoundTimer } from './useRoundTimer';
import { useRoundReveal } from './useRoundReveal';
import { startHeartbeat, stopHeartbeat } from './sfx';

export interface RushMidLiveBattleProps {
  update: RoundUpdate;
  onAction: (action: Action, round: number) => void;
  onFinished: (won: boolean) => void;
  /** replay mode: no action UI (the rounds are pre-recorded, fed from outside) */
  replay?: boolean;
}

export const RushMidLiveBattle: FC<RushMidLiveBattleProps> = ({ update, onAction, onFinished, replay = false }) => {
  const { t } = useLocalTranslation(translations);
  const haptic = useHaptic();
  const hapticRef = useRef(haptic);
  hapticRef.current = haptic;

  const initsRef = useRef<FighterInit[]>([]);
  if (update.inits?.length) initsRef.current = update.inits;
  const inits = initsRef.current;

  const youId = update.yourFighterId ?? null;
  const yourSide = inits.find((i) => i.id === youId)?.team;
  const fighters = update.fighters;
  const byId = useMemo(() => Object.fromEntries(fighters.map((f) => [f.id, f])), [fighters]);
  // cosmetic battle spot per seat — derived from the seat (the sim no longer ships coordinates)
  const pos = useMemo(() => {
    const teamSize = { t: 0, ct: 0 };
    for (const i of inits) teamSize[i.team]++;
    return new Map(inits.map((i) => [i.id, fighterLayout(i.team, i.index, teamSize[i.team])]));
  }, [inits]);
  const you = youId ? byId[youId] : null;
  const aliveEnemies = useMemo(
    () => fighters.filter((f) => f.team !== yourSide && f.alive),
    [fighters, yourSide],
  );
  // --- per-round player choice ---
  const [target, setTarget] = useState<string | null>(null);
  const targetRef = useRef<string | null>(null);
  targetRef.current = target;
  // a target on YOUR side (teammate or yourself) means you're lining up a defensive SMOKE:
  // the action menu mutes every offensive play and leaves only smoke; an enemy target is the
  // reverse. `Ref` so the auto-commit-on-timeout can branch without a stale closure.
  const targetIsAlly = !!target && byId[target]?.team === yourSide;
  const targetIsAllyRef = useRef(false);
  targetIsAllyRef.current = targetIsAlly;
  // optimistic local flag for instant feedback; the server's yourActionSubmitted
  // (from the snapshot) is the source of truth (e.g. after resume / a missed tap)
  const [localSubmitted, setLocalSubmitted] = useState(false);
  const submitted = localSubmitted || !!update.yourActionSubmitted;
  const submittedRef = useRef(false);
  submittedRef.current = submitted;
  // the action you committed THIS round — colours your own name + shows an icon by it.
  // Read by the render loop via a ref; cleared on a new round (back to a plain white name).
  const [chosenAction, setChosenAction] = useState<Action | null>(null);
  const chosenActionRef = useRef<Action | null>(null);
  chosenActionRef.current = chosenAction;
  // last stance you picked — persists across rounds so the stance orb always shows it
  const [lastStance, setLastStance] = useState<Stance>('attack');
  const round = update.round;
  const over = update.over;

  const [logOpen, setLogOpen] = useState(false);
  // события резолвнутых раундов по номеру раунда — питают шит «Ход боя».
  // Map (не массив): один и тот же update может прийти несколько раз (poll) — ключ дедупит.
  const roundsLogRef = useRef(new Map<number, RoundEvent[]>());
  useEffect(() => {
    if (update.events.length) roundsLogRef.current.set(update.round, update.events);
  }, [update.round, update.events]);

  // comeback: adrenaline shields in this round's events → the outnumbered team got a
  // round-long overshield. Surface it as a badge (⚡ Адреналин +N per fighter) so the
  // player sees why the trailing side's bars grew amber tails.
  const adrenaline = useMemo(() => {
    const shields = update.events.filter(
      (e): e is Extract<RoundEvent, { type: 'shield' }> => e.type === 'shield',
    );
    if (!shields.length) return null;
    const team: Team = shields[0].id.startsWith('ct') ? 'ct' : 't';
    return { team, amount: shields[0].amount };
  }, [update.events]);

  // clear the submit flag on a new round; the target is (re)picked AFTER the replay reveals
  // (see the replayLocked effect below), not here.
  useEffect(() => { setLocalSubmitted(false); setChosenAction(null); }, [round]);

  // missed-round heads-up (spec §4): a purely client-side heuristic, no claim logic — just
  // tells you that the round which just ended went out on the server's autopilot because you
  // never submitted. A per-round LATCH, not a live read at the boundary: `update` already
  // describes the NEW round by the time `round` changes in this render, so `submitted` at that
  // exact render reflects round N+1 (usually false), not the outgoing round N — reading it
  // there would false-flag a player who submitted round N, then reconnected (fresh mount reset
  // `localSubmitted`) and got polled again right on the N→N+1 boundary. Instead, the latch is
  // set true the moment ANY signal says you acted THIS round — local commit or the server
  // confirming `yourActionSubmitted` on ANY poll while still on that round (including the very
  // first poll after a resume) — and is only read/reset at the round transition.
  const actedThisRoundRef = useRef(false);
  useEffect(() => {
    if (submitted) actedThisRoundRef.current = true;
  }, [submitted]);
  const mountedRef = useRef(false);
  const [missedLastRound, setMissedLastRound] = useState(false);
  useEffect(() => {
    // guarded together: on the mount commit this must be a no-op, full stop — the sibling
    // latch-setter effect above may have JUST set the latch true (e.g. resuming mid-round with
    // `submitted` already true), and an unconditional reset here would wipe that out before the
    // real N→N+1 transition ever gets to read it, reintroducing the false positive.
    if (mountedRef.current) {
      setMissedLastRound(!actedThisRoundRef.current);
      actedThisRoundRef.current = false; // reset for the round that's now starting
    }
    mountedRef.current = true;
  }, [round]);

  // tapping an action button submits it immediately (no separate confirm)
  const commit = (a: Action) => {
    if (submittedRef.current || over) return;
    setLocalSubmitted(true);
    setChosenAction(a);
    onAction(a, update.round);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const [assets, setAssets] = useState<BattleAssets | null>(null);
  const map = update.map;
  useEffect(() => {
    let ok = true;
    loadBattleAssets(map).then((a) => { if (ok) setAssets(a); });
    return () => { ok = false; };
  }, [map]);

  // the render loop reads the latest update/inits/you/target via refs (a same-round poll
  // must not churn the engine effect and reset it)
  const updateRef = useRef<RoundUpdate | null>(update);
  updateRef.current = update;
  const youIdRef = useRef(youId);
  youIdRef.current = youId;

  const missLabels = useMemo(
    () => ({ miss: t('canvasMiss'), evade: t('canvasMissEvade'), blind: t('canvasMissBlind') }),
    [t],
  );
  const missLabelsRef = useRef(missLabels);
  missLabelsRef.current = missLabels;

  const { canvasRef, engineRef, field } = useBattleCanvas(assets, replay, { updateRef, initsRef, youIdRef, targetRef, chosenActionRef, hapticRef, missLabelsRef });

  const { remain, replayLocked } = useRoundTimer(engineRef, round, over, () =>
    commitRef.current(
      targetIsAllyRef.current
        ? { kind: 'smoke', smokeTargetId: targetRef.current } // teammate/self selected → smoke them
        : { kind: 'shoot', stance: 'attack', shootTargetId: targetRef.current },
    ),
  );

  // Low-HP heartbeat: loop while the LOCAL player is alive at <=15 hp. Track the HP
  // only once the round's replay has REVEALED (replayLocked false) so it follows the
  // bar the user sees, not the round-end snapshot that leads the animation — and so it
  // doesn't stutter off during every replay.
  const youHp = you?.hp;
  const youAlive = you?.alive;
  const [settledHp, setSettledHp] = useState<number | null>(null);
  const [settledAlive, setSettledAlive] = useState(true);
  useEffect(() => {
    if (replayLocked || youHp == null) return;
    setSettledHp(youHp);
    setSettledAlive(!!youAlive);
  }, [replayLocked, youHp, youAlive]);
  const heartbeatOn =
    !replay && !over && settledAlive && settledHp !== null && settledHp > 0 && settledHp <= 15;
  useEffect(() => {
    if (heartbeatOn) void startHeartbeat();
    else stopHeartbeat();
  }, [heartbeatOn]);
  useEffect(() => () => stopHeartbeat(), []);

  // (Re)pick the target only once THIS round's replay has REVEALED. `aliveEnemies` is the
  // round's END state, so a target dying this round is already "dead" there — picking on the
  // round change would jump the ring/aim to the next enemy before the current one falls on
  // screen, spoiling who dies. Keep it through the replay, re-pick after. When your target
  // died, snap to the NEAREST alive enemy (by spawn position) rather than a random one, so the
  // ring lands on a sensible next target instead of jumping across the map.
  useEffect(() => {
    if (replayLocked || replay) return; // read-only replay/spectator: no viewer, nothing to target
    setTarget((prev) => {
      // keep the current pick if it's still alive — enemy OR the teammate/self you're smoking.
      // BUT once smoke is spent, an ally target is useless: stance taps would send
      // shootTargetId=ally → the server silently shoots a random enemy. So drop an ally target
      // the moment you no longer hold a smoke, and re-pick the nearest enemy below.
      const allyNoSmoke = !!prev && byId[prev]?.team === yourSide && !you?.hasSmoke;
      if (prev && !allyNoSmoke && fighters.some((f) => f.id === prev && f.alive)) return prev;
      if (!aliveEnemies.length) return null;
      const yp = youId ? pos.get(youId) : undefined;
      let best = aliveEnemies[0].id; let bestD = Infinity;
      for (const en of aliveEnemies) {
        const ep = pos.get(en.id);
        if (!ep || !yp) continue;
        const d = Math.hypot(ep.targetX - yp.targetX, ep.y - yp.y);
        if (d < bestD) { bestD = d; best = en.id; }
      }
      return best;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayLocked, aliveEnemies]);

  const showResult = useRoundReveal(engineRef, over, round, {
    won: !!you?.alive,
    winner: update.winner ?? null,
    yourSide,
    onFinished,
  });

  // tap canvas → select the nearest ALIVE fighter — an enemy (to attack) OR a teammate (to
  // smoke; never yourself). The action menu then mutes whatever the target's side doesn't allow.
  const onCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (over || submitted || replayLocked || !you?.alive) return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // mirror the engine's stage transform EXACTLY: it fits the world into the band ABOVE the
    // action bar (rect.height - ACTION_BAR_RESERVE) and centres within it — so the hit-test
    // must use the same reduced height, or clicks land off (worst at the bottom row).
    const availH = Math.max(1, rect.height - ACTION_BAR_RESERVE);
    const s = Math.min(rect.width / WORLD_W, availH / WORLD_H);
    const ox = (rect.width - WORLD_W * s) / 2;
    const oy = (availH - WORLD_H * s) / 2;
    const wx = (e.clientX - rect.left - ox) / s;
    const wy = (e.clientY - rect.top - oy) / s;
    // teammates are only targetable when you actually hold a smoke to throw on them — otherwise
    // picking a friendly would mute every action and dead-end the turn.
    const canFriendly = !!you.hasSmoke;
    let best: string | null = null; let bestD = Infinity;
    for (const f of fighters) {
      if (!f.alive) continue;
      if (f.id === youId) continue; // you can neither shoot nor smoke yourself (self-smoke is banned)
      if (!canFriendly && f.team === yourSide) continue; // no smoke → only enemies selectable
      const p = pos.get(f.id);
      if (!p) continue;
      const d = Math.hypot(p.targetX - wx, p.y - 130 - wy);
      if (d < bestD) { bestD = d; best = f.id; }
    }
    if (best && bestD < 260) setTarget(best);
  };

  const canAct = !over && !submitted && !replayLocked && !!you?.alive;

  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      <canvas ref={canvasRef} onClick={onCanvasClick} className="h-full w-full" style={{ display: 'block' }} />

      {!assets && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-accent-blue" />
        </div>
      )}

      {over && showResult && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-xl bg-black/75 px-7 py-4 text-3xl font-extrabold tracking-wide">
            {update.winner ? (
              <>
                <span style={{ color: update.winner === 't' ? '#f0b24a' : '#5aa0ff' }}>
                  {update.winner === 't' ? 'T' : 'CT'}
                </span>
                <span className="text-white"> {t('sideWins')}</span>
              </>
            ) : (
              <span className="text-white">{t('matchDraw')}</span>
            )}
          </span>
        </div>
      )}

      {!over && !replay && (
        <div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center gap-1.5"
          style={{ top: 'calc(44px + var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))' }}
        >
          {/* round number sits up top, CS-style. During a replay `update.round` has already
              advanced to the NEXT decision round, but the animation shows the round that just
              resolved — so drop the +1 while replaying to name the round being animated. */}
          <span className="rounded-full bg-black/55 px-3 py-0.5 text-xs font-bold uppercase tracking-widest text-label-secondary backdrop-blur">
            {t('roundN', { round: replayLocked ? round : round + 1 })}
          </span>
          {replayLocked ? (
            <span className="rounded-full bg-black/60 px-4 py-1.5 text-sm font-semibold text-label-secondary backdrop-blur">
              {t('watchingReplay')}
            </span>
          ) : (
            <span
              className={cn(
                'flex h-16 min-w-16 items-center justify-center rounded-2xl bg-black/55 px-4 text-4xl font-extrabold tabular-nums backdrop-blur',
                remain <= 2 ? 'text-accent-red' : 'text-white',
              )}
            >
              {remain}
            </span>
          )}
          {roundsLogRef.current.size > 0 && (
            <button
              onClick={() => setLogOpen(true)}
              className="pointer-events-auto rounded-full bg-black/55 px-3 py-1 text-xs font-bold uppercase tracking-widest text-label-secondary backdrop-blur"
            >
              {t('roundLog')}
            </button>
          )}
        </div>
      )}

      {!over && (adrenaline || (!replay && missedLastRound && !replayLocked && !submitted && you?.alive)) && (
        <div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center gap-1.5"
          style={{ top: 'calc(150px + var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))' }}
        >
          {adrenaline && (
            <span className="flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1 text-sm font-semibold backdrop-blur">
              <FaBolt size={14} className="text-gold" />
              <span className={adrenaline.team === 't' ? 'text-gold' : 'text-accent-blue'}>{adrenaline.team === 't' ? 'T' : 'CT'}</span>
              <span className="text-label-secondary">{t('adrenaline')}</span>
              <span className="text-gold">+{adrenaline.amount}</span>
            </span>
          )}
          {/* you went out on autopilot last round — one more and you lose your share (spec §4).
              Shown only in the decision phase of a LIVE battle (never a replay/spectate — there's
              no `submitted` signal to read there); hides the instant you submit THIS round's
              action (`submitted`) without waiting for the next round-transition recompute. */}
          {!replay && missedLastRound && !replayLocked && !submitted && you?.alive && (
            <span className="rounded-full bg-accent-red/20 px-3 py-1 text-sm font-semibold text-accent-red backdrop-blur">
              {t('afkWarn')}
            </span>
          )}
        </div>
      )}

      {!replay && (
        // pinned flush to the physical bottom (ignores the safe-area inset on purpose) and
        // confined to the GAME FIELD's width (letterboxed world column), so it lines up with the
        // playfield and the top HP bar, not the black gutters. Click-through band; only the bar
        // itself is interactive.
        <div
          className="pointer-events-none absolute bottom-0 flex flex-col items-center"
          style={field.width ? { left: field.x, width: field.width } : { left: 0, right: 0 }}
        >
          {/* tapping an action submits it immediately. Kept visible (disabled) while THIS
              round's replay is still playing, so dying mid-replay doesn't yank the buttons
              before your death has animated — they hide once the round finishes and you're
              actually dead (a corpse has no turn, you just spectate the round out). */}
          {(you?.alive || replayLocked) && (
            <div className="pointer-events-auto w-full">
              <ActionMenu
                canAct={canAct}
                hasGrenade={!!you?.hasGrenade}
                hasSmoke={!!you?.hasSmoke}
                hasFlash={!!you?.hasFlash}
                hasMedkit={!!you?.hasMedkit}
                targetIsAlly={targetIsAlly}
                stance={lastStance}
                onStance={(stance) => { setLastStance(stance); commit({ kind: 'shoot', stance, shootTargetId: targetRef.current }); }}
                onKnife={() => commit({ kind: 'knife', shootTargetId: targetRef.current })}
                onGrenade={() => commit({ kind: 'grenade', grenadeTargetId: targetRef.current })}
                onSmoke={() => commit({ kind: 'smoke', smokeTargetId: targetRef.current })}
                onFlash={() => commit({ kind: 'flash', flashTargetId: targetRef.current })}
                onMedkit={() => commit({ kind: 'medkit' })}
              />
            </div>
          )}
        </div>
      )}

      {!replay && (
        <RoundLogSheet
          isOpen={logOpen}
          onClose={() => setLogOpen(false)}
          inits={inits}
          rounds={[...roundsLogRef.current.entries()].sort((a, b) => a[0] - b[0]).map(([, e]) => e)}
        />
      )}
    </div>
  );
};

export default RushMidLiveBattle;
